/**
 * Public catalog server functions — read-only, backed by the publishable client.
 * Product writes are performed through the admin service layer (Prompt 20) via
 * requireSupabaseAuth + has_role('admin').
 */
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
type Json = Database["public"]["Tables"]["products"]["Row"]["attributes"];

function publicClient() {
  const url = process.env.SUPABASE_URL!;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(url, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

export const listCategories = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();
  const { data, error } = await supabase
    .from("categories")
    .select("id, slug, name, tagline, description, cover_image, sort_order")
    .eq("is_active", true)
    .order("sort_order", { ascending: true });
  if (error) return { categories: [], error: error.message };
  return { categories: data ?? [], error: null as string | null };
});

export const listProductsByCategory = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z.object({ categoryId: z.string(), limit: z.number().int().min(1).max(200).default(48) }).parse(input),
  )
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { data: rows, error } = await supabase
      .from("products")
      .select("id, slug, name, family, gender, concentration, notes, key_notes, price_cents, price_10ml_cents, price_50ml_cents, price_100ml_cents, price_20ml_cents, currency, cover_image, badge, category_id, attributes, released_at, updated_at, discount_percent")
      .eq("is_active", true)
      .eq("category_id", data.categoryId)
      .order("released_at", { ascending: false })
      .limit(data.limit);
    if (error) return { products: [], error: error.message };
    console.info("[storefront-product-query] category", {
      categoryId: data.categoryId,
      products: (rows ?? []).map((row) => ({ id: row.id, slug: row.slug, coverImage: row.cover_image, updatedAt: row.updated_at })),
    });
    return { products: rows ?? [], error: null as string | null };
  });

export const getProductBySlug = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1) }).parse(input))
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { data: row, error } = await supabase
      .from("products")
      .select("id, slug, name, family, gender, concentration, notes, key_notes, price_cents, price_10ml_cents, price_50ml_cents, price_100ml_cents, price_20ml_cents, currency, cover_image, badge, category_id, attributes, released_at, updated_at, discount_percent")
      .eq("is_active", true)
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) return { product: null, error: error.message };
    console.info("[storefront-product-query] product", {
      slug: data.slug,
      returnedCoverImage: row?.cover_image ?? null,
      updatedAt: row?.updated_at ?? null,
    });
    return { product: row, error: null as string | null };
  });

// ---------------------------------------------------------------------------
// Prompt 15 · Collections + cross-category search
// ---------------------------------------------------------------------------

type CloudProductJson = {
  id: string; slug: string; name: string;
  family: string | null; gender: string | null; concentration: string | null;
  notes: string | null; key_notes: string[] | null;
  price_cents: number; price_10ml_cents?: number | null; price_50ml_cents?: number | null; price_100ml_cents?: number | null; price_20ml_cents?: number | null; currency: string;
  cover_image: string | null; badge: string | null; discount_percent?: number | null;
  category_id: string; attributes: Json;
};

export const listCollections = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();
  const { data, error } = await supabase
    .from("collections")
    .select(
      "id, slug, name, tagline, description, short_description, long_description, cover_image, banner_image, thumbnail_image, seo, status, is_featured, sort_order",
    )
    .eq("status", "published")
    .order("sort_order", { ascending: true });
  if (error) return { collections: [], error: error.message };
  return { collections: data ?? [], error: null as string | null };
});

export const listProductsByCollection = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z.object({ slug: z.string().min(1), limit: z.number().int().min(1).max(200).default(96) }).parse(input),
  )
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { data: coll, error: cErr } = await supabase
      .from("collections").select("id").eq("slug", data.slug).eq("status", "published").maybeSingle();
    if (cErr) return { products: [], error: cErr.message };
    if (!coll) return { products: [], error: null as string | null };
    const { data: joins, error: jErr } = await supabase
      .from("product_collections")
      .select("sort_order, products!inner(id, slug, name, family, gender, concentration, notes, key_notes, price_cents, price_10ml_cents, price_50ml_cents, price_100ml_cents, price_20ml_cents, currency, cover_image, badge, category_id, attributes, discount_percent)")
      .eq("collection_id", coll.id)
      .eq("products.is_active", true)
      .order("sort_order", { ascending: true })
      .limit(data.limit);
    if (jErr) return { products: [], error: jErr.message };
    const products = (joins ?? [])
      .map((j) => (Array.isArray(j.products) ? j.products[0] : j.products))
      .filter((p): p is CloudProductJson => p != null) as CloudProductJson[];
    return { products, error: null as string | null };
  });

export const searchAllProducts = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z.object({
      q: z.string().min(1),
      limit: z.number().int().min(1).max(100).default(48),
      categoryId: z.string().optional(),
    }).parse(input),
  )
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const term = `%${data.q.replace(/[\\%_]/g, (m) => `\\${m}`)}%`;

    // Direct column ilike on products.
    let base = supabase
      .from("products")
      .select("id, slug, name, family, gender, concentration, notes, key_notes, price_cents, price_10ml_cents, price_50ml_cents, price_100ml_cents, price_20ml_cents, currency, cover_image, badge, category_id, attributes, released_at, updated_at, discount_percent")
      .eq("is_active", true)
      .or(`name.ilike.${term},notes.ilike.${term},family.ilike.${term},brand.ilike.${term}`);
    if (data.categoryId) base = base.eq("category_id", data.categoryId);
    const columnQuery = base.limit(data.limit);

    // Attribute-index ilike on value_text — resolves matches inside jsonb
    // attributes (family/gender/concentration/format/technology/…) without a
    // full-jsonb text search.
    const idxQuery = (async () => {
      let idx = supabase
        .from("product_attribute_index")
        .select("product_id")
        .ilike("value_text", term);
      if (data.categoryId) idx = idx.eq("category_id", data.categoryId);
      const { data: rows } = await idx.limit(data.limit);
      const ids = Array.from(new Set((rows ?? []).map((r) => r.product_id)));
      if (ids.length === 0) return [] as unknown[];
      let p = supabase
        .from("products")
        .select("id, slug, name, family, gender, concentration, notes, key_notes, price_cents, price_10ml_cents, price_50ml_cents, price_100ml_cents, price_20ml_cents, currency, cover_image, badge, category_id, attributes, released_at, updated_at, discount_percent")
        .eq("is_active", true)
        .in("id", ids);
      if (data.categoryId) p = p.eq("category_id", data.categoryId);
      const { data: prods } = await p.limit(data.limit);
      return prods ?? [];
    })();

    const [colRes, attrRows] = await Promise.all([columnQuery, idxQuery as Promise<CloudProductJson[]>]);
    if (colRes.error) return { products: [], error: colRes.error.message };
    const map = new Map<string, CloudProductJson>();
    for (const r of (colRes.data ?? []) as CloudProductJson[]) map.set(r.id, r);
    for (const r of attrRows) if (!map.has(r.id)) map.set(r.id, r);
    const merged: CloudProductJson[] = Array.from(map.values()).slice(0, data.limit);
    return { products: merged, error: null as string | null };
  });
